import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import i18n from "../i18n";
import { parseLangPath, buildLangPath, DEFAULT_LANG, PREFIXED_LANGS } from "../utils/langPath";
import { getSlugForLang, resolveCanonicalSlug } from "../data/articles";

const NavigationContext = createContext();

export const useNavigation = () => useContext(NavigationContext);

// Sections whose URL slug varies per language. Extend here to add more.
const SECTION_SLUG_BY_LANG = {
  securite: { fr: "/securite", en: "/security" },
};

const pathToSection = (path) => {
  if (path === '/features') return 'features';
  if (path === '/securite' || path === '/security') return 'securite';
  if (path === '/faq') return 'faq';
  if (path === '/download') return 'download';
  if (path === '/mentions-legales') return 'mentions-legales';
  if (path === '/politique-confidentialite') return 'politique-confidentialite';
  if (path === '/conditions-utilisation') return 'conditions-utilisation';
  if (path === '/about') return 'about';
  if (path === '/trust-security') return 'trust-security';
  if (path === '/blog') return 'blog';
  if (path.startsWith('/blog/')) return 'blog-article';
  if (path === '/') return 'home';
  return 'home';
};

const sectionToPath = (section, slug = null, lang = DEFAULT_LANG) => {
  if (section === 'blog-article' && slug) return `/blog/${slug}`;
  if (section === 'blog') return '/blog';
  if (section === 'about') return '/about';
  if (section === 'trust-security') return '/trust-security';
  if (section === 'home') return '/';
  const slugMap = SECTION_SLUG_BY_LANG[section];
  if (slugMap) return slugMap[lang] || slugMap[DEFAULT_LANG] || `/${section}`;
  return `/${section}`;
};

const applyLangToI18n = (lang) => {
  if (i18n && i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }
};

export const NavigationProvider = ({ children }) => {
  const initial = typeof window !== 'undefined'
    ? parseLangPath(window.location.pathname)
    : { lang: DEFAULT_LANG, path: '/', hasExplicitLang: false };

  // Resolve initial blog slug (localize if user landed on a canonical FR slug in a non-FR lang)
  const resolveInitialSlug = () => {
    if (pathToSection(initial.path) !== 'blog-article') return null;
    const rawSlug = initial.path.replace('/blog/', '');
    const activeLang = initial.hasExplicitLang ? initial.lang : (i18n.language || DEFAULT_LANG);
    // Always resolve to the localized slug for the ACTIVE language.
    // First, normalize to canonical, then re-localize to the active lang.
    const canonical = resolveCanonicalSlug(rawSlug, activeLang);
    return getSlugForLang(canonical, activeLang);
  };

  const initialBlogSlug = resolveInitialSlug();
  const [activeSection, setActiveSection] = useState(pathToSection(initial.path));
  const [blogSlug, setBlogSlug] = useState(initialBlogSlug);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const activeSectionRef = useRef(activeSection);
  const blogSlugRef = useRef(blogSlug);
  // If the URL has an explicit language prefix, force that language.
  // Otherwise, respect whatever i18next auto-detected (localStorage → navigator → fallback).
  const langRef = useRef(initial.hasExplicitLang ? initial.lang : (i18n.language || DEFAULT_LANG));
  activeSectionRef.current = activeSection;
  blogSlugRef.current = blogSlug;

  // On first load: only override i18n when the URL explicitly declares a language.
  useEffect(() => {
    if (initial.hasExplicitLang) {
      applyLangToI18n(initial.lang);
    } else {
      // No explicit prefix → let i18next auto-detection decide (browser lang, localStorage).
      const detectedLang = i18n.language || DEFAULT_LANG;
      applyLangToI18n(detectedLang);
      langRef.current = detectedLang;
      // If auto-detection resolves to a non-default language, sync the URL so canonical/hreflang stay consistent.
      if (detectedLang !== DEFAULT_LANG) {
        const url = buildLangPath(detectedLang, initial.path);
        window.history.replaceState({ section: activeSectionRef.current, slug: blogSlugRef.current, lang: detectedLang }, '', url);
      }
    }
    // If we landed on a blog article with an old (unlocalized) slug for this
    // language, replace the URL with the canonical localized slug (soft 301).
    if (pathToSection(initial.path) === 'blog-article') {
      const rawSlug = initial.path.replace('/blog/', '');
      const activeLang = langRef.current;
      const canonical = resolveCanonicalSlug(rawSlug, activeLang);
      const expectedSlug = getSlugForLang(canonical, activeLang);
      if (rawSlug !== expectedSlug) {
        const newPath = buildLangPath(activeLang, `/blog/${expectedSlug}`);
        window.history.replaceState({ section: 'blog-article', slug: expectedSlug, lang: activeLang }, '', newPath);
      }
    }
     
  }, []);

  const navigateTo = useCallback((section, slug = null) => {
    if (section === activeSectionRef.current && slug === blogSlugRef.current) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setActiveSection(section);
      setBlogSlug(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });

      const contentPath = sectionToPath(section, slug, langRef.current);
      const url = buildLangPath(langRef.current, contentPath);
      window.history.pushState({ section, slug, lang: langRef.current }, '', url);

      setTimeout(() => setIsTransitioning(false), 50);
    }, 100);
  }, []);

  /** Change language while staying on the same content page. */
  const changeLanguage = useCallback((newLang) => {
    const prevLang = langRef.current;
    if (newLang === prevLang) return;
    langRef.current = newLang;
    applyLangToI18n(newLang);
    // If we're on a blog article, convert the slug from previous lang to new lang.
    let newSlug = blogSlugRef.current;
    if (activeSectionRef.current === 'blog-article' && blogSlugRef.current) {
      const canonical = resolveCanonicalSlug(blogSlugRef.current, prevLang);
      newSlug = getSlugForLang(canonical, newLang);
      if (newSlug !== blogSlugRef.current) {
        setBlogSlug(newSlug);
      }
    }
    // Rebuild the URL with the new language prefix and (possibly) new slug.
    const contentPath = sectionToPath(activeSectionRef.current, newSlug, newLang);
    const url = buildLangPath(newLang, contentPath);
    window.history.pushState({ section: activeSectionRef.current, slug: newSlug, lang: newLang }, '', url);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const { lang, path } = parseLangPath(window.location.pathname);
      const section = pathToSection(path);
      let slug = null;
      if (section === 'blog-article') {
        const rawSlug = path.replace('/blog/', '');
        const canonical = resolveCanonicalSlug(rawSlug, lang);
        slug = getSlugForLang(canonical, lang);
      }
      if (lang !== langRef.current) {
        langRef.current = lang;
        applyLangToI18n(lang);
      }
      setActiveSection(section);
      setBlogSlug(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const contextValue = useMemo(
    () => ({ activeSection, blogSlug, navigateTo, changeLanguage, isTransitioning }),
    [activeSection, blogSlug, navigateTo, changeLanguage, isTransitioning]
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export const PageSection = ({ id, children }) => {
  const { activeSection, isTransitioning } = useNavigation();
  const isActive = activeSection === id;

  if (!isActive) return null;

  return (
    <div
      className={`page-section ${isActive ? 'active' : ''} ${isTransitioning ? 'transitioning' : ''}`}
      id={`${id}-section`}
    >
      {children}
    </div>
  );
};

export const SPALink = ({ to, children, className, onClick, ...props }) => {
  const { navigateTo, activeSection } = useNavigation();
  const currentLang = i18n.language || DEFAULT_LANG;

  // Compute the language-aware href for display (right-click, open-in-new-tab, SEO).
  // The click handler still works from the original `to`, but the rendered <a href>
  // reflects the actual destination the user will land on.
  const displayHref = useMemo(() => {
    if (to.startsWith('/#') || !to.startsWith('/')) return to;
    if (to.startsWith('/blog/')) {
      const slug = to.replace('/blog/', '');
      const canonical = resolveCanonicalSlug(slug, DEFAULT_LANG);
      const langSlug = getSlugForLang(canonical, currentLang);
      return buildLangPath(currentLang, `/blog/${langSlug}`);
    }
    const section = pathToSection(to);
    const contentPath = sectionToPath(section, null, currentLang);
    return buildLangPath(currentLang, contentPath);
  }, [to, currentLang]);

  const handleClick = (e) => {
    e.preventDefault();

    if (to.startsWith('/#')) {
      const elementId = to.substring(2);
      if (activeSection === 'home') {
        const element = document.getElementById(elementId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigateTo('home');
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) element.scrollIntoView({ behavior: 'smooth' });
        }, 200);
      }
      return;
    }

    if (to.startsWith('/blog/')) {
      const slug = to.replace('/blog/', '');
      navigateTo('blog-article', slug);
      if (onClick) onClick(e);
      return;
    }

    const section = pathToSection(to);
    navigateTo(section);
    if (onClick) onClick(e);
  };

  return (
    <a href={displayHref} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
};

export { PREFIXED_LANGS };
